import { useEffect, useRef, useState } from 'react';
import Board from '../components/Board';
import { SIZE } from '../config';
import { PlayerColor, playerColorToString, stringToPlayerColor } from '../types';
import * as SockerUtils from '../utils/SocketUtils';
import checkWinner from '../utils/CheckWinner';
import * as FetchUtils from '../utils/FetchUtils' ;

type Cell = PlayerColor;
const PLAYER_COUNT_READY_TO_START = 2;

function Game() {
  const [playerColor, setPlayerColor] = useState<PlayerColor>(null);
  const [currentTurn, setCurrentTurn] = useState<PlayerColor>('black');
  const [winner, setWinner] = useState<PlayerColor>(null);
  const [numOfTurn, setNumOfTurn] = useState<number>(0);
  const [board, setBoard] = useState<Cell[][]>(
    Array.from({ length: SIZE }, () => Array(SIZE).fill(null))
  );
  const [hasJoined, setHasJoined] = useState<boolean>(false);
  const [playerCount, setPlayerCount] = useState<number>(0);
  const initialized = useRef(false);
  const playerColorRef = useRef(playerColor);

  async function updatePlayerCount(currentPlayerCount: number) {
    if (currentPlayerCount !== PLAYER_COUNT_READY_TO_START) {
      const fetchedPlayerCount: number = await FetchUtils.fetchPlayerCount();
      setPlayerCount(fetchedPlayerCount);
    }
  };

  function updateBoardWithMove(row: number, col: number, currentPlayer: PlayerColor): void {
    setBoard(prev => {
      const next = prev.map(row => [...row]);
      next[row][col] = currentPlayer;
      return next;
    });
  }

  function updateNumOfTurn(): void {
    // console.log(`>Increment number of turn: ${numOfTurn+1}`);
    setNumOfTurn(prev => prev += 1);
  }

  //FIXME: No state being called with subscription isn't up to date
  function updateStateWithMove(row: number, col: number, color: PlayerColor) {
    console.log(`>updateStateWithMove called for Row: ${row}, Col: ${col}, color: ${color}`);
    updateBoardWithMove(row, col, color);
    setCurrentTurn(prev => (prev === 'black' ? 'white' : 'black'));
    const result = checkWinner(board, row, col, color);
    if (result) setWinner(color);
    updateNumOfTurn();
  }

  function moveSocketAction(message: SockerUtils.Move): void {
    const { row, col, color } = message;
    const currentPlayer = playerColorRef.current;
    console.log(`${numOfTurn}. moveSocketAction called for Row: ${row}, Col: ${col}, Color: ${color}, CurrentPlayer: ${currentPlayer}`);
    const previousPlayer = stringToPlayerColor(color); // Action from the previous turn
    if (previousPlayer === currentPlayer) {
      console.log('>Ignore event: Color is same as the player color');
      return;
    }

    updateStateWithMove(row, col, previousPlayer);
  }

  function joinGame() {
    console.log(`joinGame called with PlayerCount: ${playerCount}`);
    if (playerCount === PLAYER_COUNT_READY_TO_START) {
      console.log(`>Failed to join: player count is already 2`);
      return;
    }

    let color: PlayerColor = null;
    if (playerCount === 0) {
      color = 'black';
    } else if (playerCount === 1) {
      color = 'white';
    }
    setPlayerColor(color);
    console.log(`>Set player color to ${color}`);
    SockerUtils.connect({
      moveTopic: moveSocketAction,
      disconnectTopic: () => {
        console.log("Disconnect topic triggered");
        setPlayerCount(c => c - 1);
      },
    });
    setHasJoined(true);
  }

  // Handle opponent moves from WebSocket
  useEffect(() => {
    // Code here runs after the component renders
    console.log("useEffect called");
    if (!initialized.current) {
      initialized.current = true
      //TOOD: Add logic whether if it needs to be called on app start.
    }

    updatePlayerCount(playerCount);
    const interval = setInterval(updatePlayerCount, 3000, playerCount); // refresh every 3s
    return () => clearInterval(interval);
  }, [playerCount]);

  useEffect(() => {
    // Code here runs after the component renders
    console.log(`playerColor got updated: ${playerColor}`);
    playerColorRef.current = playerColor;
  }, [playerColor]);

  const handleCellClick = (row: number, col: number) => {
    console.log(`${numOfTurn}. handleCellClick called with row: ${row}, col: ${col}`);
    if (winner !== null || 
      board[row][col] !== null || 
      playerColor !== currentTurn ||
      playerCount !== PLAYER_COUNT_READY_TO_START) {
      if (winner !== null) {
        console.log(`>Move ignored: game ended with winner: ${winner}`);
      }
      if (board[row][col] !== null) {
        console.log(`>Move ignored: cell occupied for row: ${row}, col: ${col}, cell: ${board[row][col]}`);
      }
      if (playerColor !== currentTurn) {
        console.log(`>Move ignored: current turn isn't for the given player`);
      }
      if (playerCount !== PLAYER_COUNT_READY_TO_START) {
        console.log(`>Move ignored: waiting for the player to start: CurrentPlayerCount: ${playerCount}`);
      }
      return;
    }

    updateStateWithMove(row, col, currentTurn);

    SockerUtils.sendMove({ 
      row: row,
      col: col,
      color: playerColorToString(currentTurn),
    });
  };

  console.debug("Game page is loaded");
  return (
    <div className="text-center p-4">
      <h1 className="text-2xl font-bold mb-4">Gomoku (Five in a Row)</h1>
      {!hasJoined ? (
        <button
          className="bg-blue-500 text-white px-4 py-2 rounded"
          onClick={joinGame}
        >
          Join Game
        </button>
      ) : (
        <>
          <p className="mb-2 text-gray-600">Players joined: {playerCount}</p>
          <p>Your color: <strong style={{ color: playerColor === 'black' ? 'black' : 'white', backgroundColor: playerColor === 'white' ? 'black' : 'white', padding: '4px' }}>{playerColor}</strong></p>
          {winner ? (
            <p className="text-xl text-green-600 font-semibold">{winner.toUpperCase()} Wins!</p>
          ) : (
            <p className="mb-2">{currentTurn?.toUpperCase()}'s Turn</p>
          )}
          <Board board={board} onCellClick={handleCellClick} />
        </>
      )}
    </div>
  );
}

export default Game;
